// Fixed bottom navigation for mobile (md:hidden). Lives at the
// AppShell level so it's present on every logged-in client page.
//
// Four tabs (the Fresha layout): Acasă, Caută, Programări, Profil. "Caută"
// opens the full-screen map (HomePage's ?view=map), the same way Fresha's
// Search tab drops you onto the map + venue sheet. Shown for EVERYONE on mobile
// (md:hidden hides it on desktop) — guests included; their Programări/Profil
// tabs route to /login. The header's top-right menu is hidden for guests on
// mobile so the two don't duplicate (see AppHeader).
//
// Active state matches by route prefix + the ?view=map flag so the map and
// the grid light up the right tab even though both live on "/".

import { Link, useLocation } from 'react-router-dom';
import { FiHome, FiSearch, FiCalendar, FiUser } from 'react-icons/fi';
import useAuth from '../../hooks/useAuth';
import { useLoginDrawer } from '../auth/LoginDrawer';
import { isClientNativeApp } from '../../utils/platform';

export default function BottomNav() {
  const location = useLocation();
  const { user } = useAuth();
  const { open: openLoginDrawer } = useLoginDrawer();

  // This is the CLIENT tab bar (Acasă/Caută/Programări/Profil). It's kept out of
  // the Business app at the MOUNT point — AppShell renders `{!isBusinessApp &&
  // <BottomNav/>}` — so this component (and its hooks/context lookups) never runs
  // there at all, rather than mounting and early-returning null.
  const path = location.pathname;
  // The salon detail page is an immersive Fresha-style view: its own sticky
  // header + tab bar and a bottom "Rezervă" bar replace the global chrome, so
  // the tab nav is hidden there (mobile).
  if (path.startsWith('/salon/')) return null;
  // The marketplace surface renders at "/", "/search" and "/map"; the map vs
  // grid split is the ?view=map flag, so Acasă and Caută share the surface and
  // are disambiguated by that param.
  const onHomeSurface = path === '/' || path.startsWith('/search') || path.startsWith('/map');
  // The dedicated /map route IS the map, even without the ?view=map flag — match
  // it directly. Otherwise landing on /map (deep link / shared URL) would leave
  // isMap false and wrongly light up "Acasă" instead of "Caută".
  const isMap = /^\/map\/?$/.test(path) || (onHomeSurface && new URLSearchParams(location.search).get('view') === 'map');
  const isHome = onHomeSurface && !isMap;
  const isBookings = path.startsWith('/my-bookings') || path.startsWith('/booking');
  const isAccount = path.startsWith('/profile');

  const tabs = [
    { to: '/', Icon: FiHome, label: 'Acasă', active: isHome },
    { to: '/?view=map', Icon: FiSearch, label: 'Caută', active: isMap },
    { to: '/my-bookings', Icon: FiCalendar, label: 'Programări', active: isBookings },
    // Guests: Profil opens the login drawer over the current screen (and lands
    // on /profile after a successful login). Logged-in: normal navigation.
    user
      ? { to: '/profile', Icon: FiUser, label: 'Profil', active: isAccount }
      : { onClick: () => openLoginDrawer({ redirectTo: '/profile' }), Icon: FiUser, label: 'Profil', active: isAccount },
  ];

  return (
    <nav
      aria-label="Navigare mobilă"
      // On the map (?view=map) the bar floats above the full-screen map (z-60)
      // and the draggable venue sheet (z-65), the Fresha pattern; everywhere
      // else it sits at z-40 so modals/drawers (z-50+) still cover it.
      // `md:hidden` hides the mobile bar on WEB desktop (where the header's
      // top-right menu takes over). But the CLIENT native app has no top menu
      // (AppHeader.showAccountMenu = !isNativeApp), so on "iOS app on Mac" — a
      // native build in a wide window — hiding it at md left the user with NO
      // navigation at all. Keep it at every width on the client native app.
      // (The Business app never mounts this — see AppShell.)
      className={`fixed bottom-0 left-0 right-0 ${isMap ? 'z-[70]' : 'z-40'} border-t border-ink/10 bg-cream-100/95 backdrop-blur pb-[env(safe-area-inset-bottom,0)] ${isClientNativeApp ? '' : 'md:hidden'}`}
    >
      <div className="mx-auto grid max-w-md grid-cols-4">
        {tabs.map((tab) => (
          <Tab key={tab.label} {...tab} />
        ))}
      </div>
    </nav>
  );
}

function Tab({ to, onClick, Icon, label, active }) {
  const className = `flex flex-col items-center gap-1 rounded-lg px-1 py-2 text-[10px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-inset ${
    active ? 'text-primary-700' : 'text-ink-500'
  }`;
  const inner = (
    <>
      <span className={`flex h-7 w-14 items-center justify-center rounded-full transition-colors ${active ? 'bg-primary-100' : ''}`}>
        <Icon className={`h-5 w-5 ${active ? '' : 'opacity-70'}`} aria-hidden="true" />
      </span>
      <span>{label}</span>
    </>
  );
  // Profil (guest) is a button that opens the login drawer; the rest navigate.
  return onClick
    ? <button type="button" onClick={onClick} className={className}>{inner}</button>
    : <Link to={to} className={className}>{inner}</Link>;
}
